import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { loadIndex } from "../data/loader.js";
import type { DiaryListItem, Language } from "../data/types.js";
import { t } from "../lib/strings.js";

interface Props {
  lang: Language;
}

export function DiaryList({ lang }: Props): JSX.Element {
  const s = t(lang);
  const [entries, setEntries] = useState<DiaryListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadIndex()
      .then(setEntries)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 text-red-700">
        {s.errorPrefix}: {error}
      </div>
    );
  }
  if (!entries) {
    return <div className="max-w-6xl mx-auto px-4 py-8 text-ink-muted">{s.loadingDiaries}</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="font-serif text-3xl mb-2">{s.diariesTitle}</h1>
        <p className="text-ink-muted text-sm max-w-3xl">{s.diariesSubtitle}</p>
      </header>

      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {entries.map((entry) => (
          <li key={entry.slug}>
            <Link
              to={`/diary/${entry.slug}`}
              className="block bg-white rounded-md shadow-sm border border-parchment-200 hover:border-parchment-300 hover:shadow-md transition overflow-hidden"
            >
              {entry.thumbUrl ? (
                <img
                  src={entry.thumbUrl}
                  alt={entry.title}
                  className="w-full h-48 object-cover bg-parchment-100"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-48 bg-parchment-100 flex items-center justify-center text-ink-muted text-sm">
                  {s.noThumbnail}
                </div>
              )}
              <div className="p-3">
                <div className="font-serif text-lg">{entry.title}</div>
                {entry.dateLabel && (
                  <div className="text-sm text-ink-muted">{entry.dateLabel}</div>
                )}
                <div className="text-xs mt-1 flex gap-1 flex-wrap">
                  {entry.languages.map((l) => (
                    <span
                      key={l}
                      className="px-1.5 py-0.5 bg-parchment-100 rounded uppercase tracking-wider"
                    >
                      {l}
                    </span>
                  ))}
                  <span className="px-1.5 py-0.5 bg-parchment-100 rounded text-ink-muted">
                    {s.pagesCount(entry.pageCount)}
                  </span>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
